import i18n from 'i18next';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ADD_USER_DAMAGE from 'components/damages/DamageEdit/AddUserDamage.gql';
import { ExpandedDamage } from 'components/damages/ExpandedDamage/ExpandedDamage';
import { useDamageAttrs } from 'components/damages/withDamages';
import { limitFiltersFromData } from 'components/data/helpers';
import DataTable from 'components/DataTable';
import { EFilterSortClearType } from 'components/DataTable/types';
import { DAMAGE_EDIT_ATTRIBUTES } from 'utils/access-control/rules';
import { useMutation } from 'utils/apollo';
import { BulkEdit } from 'utils/editable';
import { SpacedContainer } from 'utils/layouts/containers';
import { RenderWithAccess } from 'utils/usePermissions';

const CONFIRMED_TAB_LIST = [
  {
    key: 'ALL',
    label: i18n.t('table.all', 'All'),
    value: {
      filters: { isConfirmed: undefined },
      sortOrder: { columnKey: 'Severity', order: 'descend' },
    },
  },
  {
    key: 'CONFIRMED',
    label: i18n.t('table.confirmed', 'Confirmed'),
    value: { filters: { isConfirmed: ['true'] } },
  },
  {
    key: 'UNCONFIRMED',
    label: i18n.t('table.unconfirmed', 'Unconfirmed'),
    value: { filters: { isConfirmed: ['false'] } },
  },
  {
    key: 'SEVERE',
    label: undefined,
    value: {
      filters: { Severity: [[4]] },
      sortOrder: { columnKey: 'Severity', order: 'descend' },
    },
  },
  {
    key: 'DATE_DESC',
    label: undefined,
    value: {
      sortOrder: { columnKey: 'Date', order: 'descend' },
    },
  },
];

const expandedRowRender = (damage) => <ExpandedDamage damageId={damage.id} />;

export function DamageTable({
  columns = [],
  data = [],
  dataQuery,
  loading = false,
  actions,
  defaultActiveTabKey = 'ALL',
  schema,
  selected = [],
  onClearSelection = () => {},
  additionalDropdownActions = [],
  ...props
}) {
  const { t } = useTranslation();
  const { damageColumns, loading: loadingAttrs } = useDamageAttrs(schema);

  const allColumns = useMemo(
    () =>
      [
        ...columns,
        ...damageColumns.map((col) => (dataQuery ? col : limitFiltersFromData(col, data))),
      ].map((c) => ({
        ...c,
      })),
    [columns, damageColumns, dataQuery, data]
  );

  const [addUserDamage] = useMutation(ADD_USER_DAMAGE);

  const handleBulkEditSubmit = async (values) => {
    await Promise.all(
      selected.map((damage) => {
        const mergedAttrs = { ...damage.attrs, ...values };
        const attrs = Object.keys(mergedAttrs)
          .map((key) => {
            const value = mergedAttrs[key];

            if (typeof value === 'object' && 'toDate' in value) {
              return { name: key, date: value.format('YYYY-MM-DD') };
            }
            if (typeof value === 'number') {
              return { name: key, number: value };
            }

            return { name: key, string: value };
          })
          .filter(
            (attr) =>
              (attr?.date && !isEmpty(attr.date)) ||
              (attr?.number && isFinite(attr.number)) ||
              (attr?.string && !isEmpty(attr.string))
          );

        const { id, schema, observations } = damage;
        const schemaId = schema?.id;
        const { id: observationId, coordinates, picture } = observations[0];
        const pictureId = picture?.id;

        return addUserDamage({
          variables: {
            damage: {
              branchGlobalId: id,
              schemaId,
              attrs,
              annotations: [
                {
                  branchGlobalId: observationId,
                  coordinates,
                  pictureId,
                },
              ],
            },
          },
        });
      })
    );

    onClearSelection();
  };

  return (
    <DataTable
      loading={loading || loadingAttrs}
      columns={allColumns}
      expandable={{ expandedRowRender }}
      data={data}
      dataQuery={dataQuery}
      additionalDropdownActions={additionalDropdownActions}
      actions={
        <>
          {actions}
          {!!selected.length && (
            <SpacedContainer>
              <RenderWithAccess rules={[DAMAGE_EDIT_ATTRIBUTES]}>
                <BulkEdit
                  records={selected.map(({ id, attrs }) => ({ id, ...attrs }))}
                  columns={damageColumns}
                  onSubmit={handleBulkEditSubmit}
                />
              </RenderWithAccess>
            </SpacedContainer>
          )}
        </>
      }
      emptyText={t('damage.noDamagesYet', 'You currently have no damages.')}
      tabList={CONFIRMED_TAB_LIST.filter(({ label }) => label)}
      initialPresetFilter={CONFIRMED_TAB_LIST.find((tab) => tab.key === defaultActiveTabKey)}
      clearSortAndFilterType={EFilterSortClearType.RESET}
      {...props}
    />
  );
}
